import './App.css';
import {useState, useEffect} from 'react';
import { ThemeProvider } from '@mui/material/styles';
import Launcher from './components/Launcher'
import CallSetup from './components/CallSetup'
import CreateMuiTheme from './themes/Default'
import { ConfigProvider } from './contexts/configContext';
import { getDayString } from './helpers/DateHelpers'
import { hexToRgbA } from './helpers/ColorHelpers'
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

function App() {
  const [config, setConfig] = useState(null)
  const [launcherVisible, setLauncherVisible] = useState(true)
  const [launcherFullSize, setLauncherFullSize] = useState(false)
  const [callSetupStarted, setCallSetupStarted] = useState(false)
  const [theme, setTheme] = useState(CreateMuiTheme(null))
  const [timeConditions, setTimeConditions] = useState(null)
  const [currentTimeIntervalId, setCurrentTimeIntervalId] = useState(null)
  const [tcActive, setTcActive] = useState(false)
  const [maintenance, setMaintenance] = useState(false)
  const [meetingId, setMeetingId] = useState(null)
  const [pin, setPin] = useState(null)
  const [maintenanceMode, setMaintenanceMode] = useState('nodisplay')

  useEffect(() => {
    if (!window.LiveCallConfig) {
      console.log('[Live Call] No configuration found, exiting.')
    }
    console.log('[Live Call] Config detected, loading...')
    setConfig(window.LiveCallConfig)
    if (window.LiveCallConfig.enableLinkHandling) handleUrlParams()
  }, [])

  useEffect(() => {
    if (!config) return

    if (config.colors) {
      for (const key in config.colors) {
        // console.log(`${key}: ${config.colors[key]}`);
        document.documentElement.style.setProperty(key, config.colors[key]);
        if (key === '--live-call-color-primary-main') {
          document.documentElement.style.setProperty('--live-call-color-primary-main-90pc', hexToRgbA(config.colors[key], 0.9));
          document.documentElement.style.setProperty('--live-call-color-primary-main-80pc', hexToRgbA(config.colors[key], 0.8));
          document.documentElement.style.setProperty('--live-call-color-primary-main-70pc', hexToRgbA(config.colors[key], 0.7));
          document.documentElement.style.setProperty('--live-call-color-primary-main-60pc', hexToRgbA(config.colors[key], 0.6));
          document.documentElement.style.setProperty('--live-call-color-primary-main-50pc', hexToRgbA(config.colors[key], 0.5));
          document.documentElement.style.setProperty('--live-call-color-primary-main-40pc', hexToRgbA(config.colors[key], 0.4));
          document.documentElement.style.setProperty('--live-call-color-primary-main-30pc', hexToRgbA(config.colors[key], 0.3));
          document.documentElement.style.setProperty('--live-call-color-primary-main-20pc', hexToRgbA(config.colors[key], 0.2));
          document.documentElement.style.setProperty('--live-call-color-primary-main-10pc', hexToRgbA(config.colors[key], 0.1));
          document.documentElement.style.setProperty('--live-call-color-primary-main-00pc', hexToRgbA(config.colors[key], 0.0));
        }
      }
      setTheme(CreateMuiTheme(config.colors))
    }

    if (config.timeConditions) {
      setTimeConditions(config.timeConditions)
    } else {
      setTcActive(true)
    }

    if (config.maintenance) {
      if (config.maintenance.enabled) setMaintenance(true)
      if (config.maintenance.mode) setMaintenanceMode(config.maintenance.mode)
    }
  }, [config])

  useEffect(() => {
    if (!timeConditions) return
    let intervalId = setInterval(() => {
      let currentlyActive = isTcActive()
      if (tcActive !== currentlyActive) {
        setTcActive(currentlyActive)
      }
    }, 1000)
    setCurrentTimeIntervalId(intervalId)
    return () => clearInterval(currentTimeIntervalId)
  }, [timeConditions, tcActive])

  const handleUrlParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const meetingId = urlParams.get('meetingId')
    const pin = urlParams.get('pin')
    if (meetingId) {
      setMeetingId(meetingId)
      setCallSetupStarted(true)
      setLauncherVisible(false) 
    }
    if (pin) setPin(pin)
  }

  const isTcActive = () => {
    if (!timeConditions) return

    let now = new Date()
    let dayString = getDayString(now.getUTCDay())
    let isActive = timeConditions[dayString]?.some(condition => {
      let activate = condition.activate
      let deactivate = condition.deactivate

      let activateDate = new Date(
        Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate(),
          activate.hour,
          activate.minute,
          activate.second
        )
      )

      let deactivateDate = new Date(
        Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate(),
          deactivate.hour,
          deactivate.minute,
          deactivate.second
        )
      )

      return now >= activateDate && now < deactivateDate
    });
    return isActive ? true : false

  }

  return (
    <ThemeProvider theme={theme}>
      <ConfigProvider value={config}>
        <div className="App">
          {tcActive 
          && launcherVisible
          && !(maintenance & maintenanceMode === 'nodisplay') ? 
            <Launcher
              fullSize={launcherFullSize}
              maintenance={maintenance}
              maintenanceMode={maintenanceMode}
              onFullSizeChange={ state => { setLauncherFullSize(state) }}
              onStartCall={ () => {
                setCallSetupStarted(true)
                setLauncherVisible(false) 
              }}
            />
          : null}
          
          {callSetupStarted ? 
            <CallSetup
              meetingId={meetingId}
              pin={pin}
              onClose={ () => {
                setCallSetupStarted(false)
                setLauncherVisible(true) 
                setLauncherFullSize(false) 
                setMeetingId(null)
                setPin(null)
              }}
              onMeetingIdChange={(id) => setMeetingId(id)}
              onPINChange={(pin) => setPin(pin)}
            />
          : null}
        </div>
      </ConfigProvider>
    </ThemeProvider>
  );
}

export default App;
